import { useState } from "react";
import moment from "moment-timezone";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader } from "@/components/ui/loader";
import { useToast } from "@/components/ui/use-toast";
import { CommonDestinationTimezone } from "@/lib/utils";
import { OtcContractLayerDto } from "@/lib/api/otc";
import {
  OtcAmendmentReviewListItem,
  OtcAmendmentReviewTask,
  useSetOtcAmendmentReviewTaskCompletion,
  useMarkOtcAmendmentLayerReviewed,
} from "@/lib/api/otcAmendmentReview";
import { OtcContractLayerCard } from "./contract-detail-view/OtcContractLayerCard";

interface AmendmentReviewDetailModalProps {
  item: OtcAmendmentReviewListItem | null;
  open: boolean;
  onClose: () => void;
}

function ReviewTaskRow({
  task,
  contract_id,
  pending_layer_id,
  disabled,
}: {
  task: OtcAmendmentReviewTask;
  contract_id: string;
  pending_layer_id: string;
  disabled: boolean;
}) {
  const { toast } = useToast();
  const setTaskCompletion = useSetOtcAmendmentReviewTaskCompletion();
  const [isSaving, setIsSaving] = useState(false);

  const onToggle = async (next: boolean) => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      const result = await setTaskCompletion(
        {
          layer_id: pending_layer_id,
          task_instance_id: task.id,
          is_completed: next,
        },
        contract_id,
      );

      if (!result.success) {
        toast({ title: "Failed to update task", description: result.message || "" });
      }
    } catch (error: unknown) {
      console.error("Failed to update task", error);
      toast({ title: "Failed to update task", description: "" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-3 py-2 border-b border-border last:border-b-0">
      {isSaving ? (
        <Loader svgClassName="w-4 h-4 animate-spin" />
      ) : (
        <Checkbox
          checked={task.is_completed}
          disabled={disabled}
          onCheckedChange={(checked: boolean | "indeterminate") => onToggle(checked === true)}
        />
      )}
      <div className="flex flex-col">
        <span className="text-sm font-medium">{task.name}</span>
        {task.is_completed && task.completed_by_user_name && (
          <span className="text-xs text-muted-foreground">
            Completed by {task.completed_by_user_name}
            {task.completed_at
              ? ` on ${moment.tz(task.completed_at, CommonDestinationTimezone).format("MM/DD/YYYY h:mm A")}`
              : ""}
          </span>
        )}
      </div>
    </div>
  );
}

export function AmendmentReviewDetailModal({
  item,
  open,
  onClose,
}: AmendmentReviewDetailModalProps) {
  const { toast } = useToast();
  const markReviewed = useMarkOtcAmendmentLayerReviewed();
  const [isMarking, setIsMarking] = useState(false);

  const onOpenChange = (next: boolean) => {
    if (!next) {
      onClose();
    }
  };

  const contractId = item ? (item.contract.id ?? "") : "";
  const contractNo = item?.contract.contract_no ?? null;
  const allComplete = item ? (item.tasks.length === 0 || item.all_tasks_completed) : false;

  const onMarkReviewed = async () => {
    if (!item || isMarking) return;
    setIsMarking(true);

    try {
      const result = await markReviewed(
        { layer_id: item.pending_layer_id },
        contractId,
      );

      if (!result.success) {
        toast({ title: "Failed to mark reviewed", description: result.message || "" });
      } else {
        toast({ title: "Amendment marked reviewed", description: "" });
        onClose();
      }
    } catch (error: unknown) {
      console.error("Failed to mark reviewed", error);
      toast({ title: "Failed to mark reviewed", description: "" });
    } finally {
      setIsMarking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-none w-[90vw] h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>
            Amendment Review {contractNo ? `\u2014 OTC Contract #${contractNo}` : ""}
          </DialogTitle>
        </DialogHeader>

        <div className="grow overflow-y-auto pr-1 flex flex-col gap-4">
          {!item && (
            <div className="text-sm text-muted-foreground">No amendment selected.</div>
          )}

          {item && (
            <>
              <div className="rounded-lg border p-4 flex flex-col gap-1">
                <span className="font-medium mb-2">Review Checklist</span>
                {item.tasks.length === 0 ? (
                  <span className="text-sm text-muted-foreground">
                    No review tasks configured.
                  </span>
                ) : (
                  item.tasks.map((task: OtcAmendmentReviewTask) => (
                    <ReviewTaskRow
                      key={task.id}
                      task={task}
                      contract_id={contractId}
                      pending_layer_id={item.pending_layer_id}
                      disabled={isMarking}
                    />
                  ))
                )}
              </div>

              <div className="flex flex-col gap-3">
                <span className="font-medium">Layer History</span>
                {item.layers.map((layer: OtcContractLayerDto) => (
                  <OtcContractLayerCard key={layer.id} layer={layer} />
                ))}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="shrink-0">
          <Button variant="outline" type="button" onClick={onClose}>
            Close
          </Button>
          <Button
            type="button"
            disabled={!item || !allComplete || isMarking}
            onClick={onMarkReviewed}
          >
            {isMarking ? <Loader svgClassName="w-4 h-4" /> : "Mark as Reviewed"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
